import { useEffect, useRef, useState, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import type { ReviewPolygon } from "@/types/document-review";
import { useTranslation } from "react-i18next";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Zoom constants
const MIN_SCALE = 0.5;
const MAX_SCALE = 3.0;
const SCALE_STEP = 0.1;

// Rotation constants
const ROTATION_STEP = 90;
type RotationDegrees = 0 | 90 | 180 | 270;

// Configura il worker una sola volta
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PdfViewerProps = {
    fileUrl: string;
    polygons: ReviewPolygon[];
    renderOverlay?: (args: {
        pageNumber: number;
        pageWidth: number;
        pageHeight: number;
        containerRef: React.RefObject<HTMLDivElement>;
    }) => React.ReactNode;
    onCreatePolygon?: (options?: { pageNumber?: number; x?: number; y?: number }) => void;
    onPageChange?: (page: number) => void;
    currentPage?: number; // Prop opzionale per controllare la pagina dall'esterno
    hideAddRectangle?: boolean; // Nasconde il pulsante "Aggiungi Rettangolo"
    showZoomControls?: boolean; // Mostra i controlli zoom
    initialScale?: number; // Scala iniziale (default 1.0)
};

export function PdfViewer({ fileUrl, renderOverlay, onCreatePolygon, onPageChange, currentPage: controlledCurrentPage, hideAddRectangle = false, showZoomControls = false, initialScale = 1.0 }: PdfViewerProps) {
    const { t } = useTranslation('common');
    const [numPages, setNumPages] = useState<number>(0);
    const [scale, setScale] = useState<number>(initialScale);
    const [rotation, setRotation] = useState<RotationDegrees>(0);
    const [internalCurrentPage, setInternalCurrentPage] = useState<number>(1);
    const [pageDimensions, setPageDimensions] = useState<Record<number, { width: number; height: number }>>({});
    // Floating toolbar state removed - main toolbar is always visible
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; pageNumber: number; clientX: number; clientY: number } | null>(null);

    // Usa la prop controllata se fornita, altrimenti usa lo stato interno
    const currentPage = controlledCurrentPage ?? internalCurrentPage;
    // Safe page ensures we never try to render an invalid page
    const safePage = numPages > 0 ? Math.min(Math.max(1, currentPage), numPages) : currentPage;
    const setCurrentPage = (page: number) => {
        const boundedPage = numPages > 0 ? Math.min(Math.max(1, page), numPages) : page;
        setInternalCurrentPage(boundedPage);
        onPageChange?.(boundedPage);
    };

    const containerRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const addButtonRef = useRef<HTMLButtonElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const handleDocumentLoad = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handlePageRenderSuccess = (pageNumber: number) => {
        // Use requestAnimationFrame to ensure DOM is fully updated
        requestAnimationFrame(() => {
            const container = containerRefs.current[pageNumber];
            if (container) {
                const width = container.clientWidth;
                const height = container.clientHeight;
                if (width > 0 && height > 0) {
                    setPageDimensions((prev) => ({
                        ...prev,
                        [pageNumber]: { width, height },
                    }));
                }
            }
        });
    };

    const handleCreateClick = () => {
        onCreatePolygon?.({ pageNumber: safePage });
    };

    // Zoom handlers
    const handleZoomIn = useCallback(() => {
        setScale((prev) => Math.min(MAX_SCALE, prev + SCALE_STEP));
    }, []);

    const handleZoomOut = useCallback(() => {
        setScale((prev) => Math.max(MIN_SCALE, prev - SCALE_STEP));
    }, []);

    const handleZoomReset = useCallback(() => {
        setScale(1.0);
    }, []);

    // Rotation handlers
    const handleRotateClockwise = useCallback(() => {
        setRotation((prev) => ((prev + ROTATION_STEP) % 360) as RotationDegrees);
    }, []);

    const handleRotateCounterClockwise = useCallback(() => {
        setRotation((prev) => ((prev - ROTATION_STEP + 360) % 360) as RotationDegrees);
    }, []);

    // Reset state when fileUrl changes to avoid stale worker errors
    useEffect(() => {
        setNumPages(0);
        setPageDimensions({});
        setInternalCurrentPage(1);
        setRotation(0);
        containerRefs.current = {};
    }, [fileUrl]);

    // Sincronizza lo stato interno quando cambia la prop controllata
    useEffect(() => {
        if (controlledCurrentPage !== undefined && controlledCurrentPage !== internalCurrentPage) {
            setInternalCurrentPage(controlledCurrentPage);
        }
    }, [controlledCurrentPage, internalCurrentPage]);

    // Normalizza currentPage quando numPages viene caricato o cambia
    useEffect(() => {
        if (numPages > 0 && internalCurrentPage > numPages) {
            setInternalCurrentPage(numPages);
        }
        if (numPages > 0 && internalCurrentPage < 1) {
            setInternalCurrentPage(1);
        }
    }, [numPages, internalCurrentPage]);

    // Notifica il cambiamento di pagina quando cambia lo stato interno o la prop
    useEffect(() => {
        onPageChange?.(currentPage);
    }, [currentPage, onPageChange]);


    // Floating toolbar observer removed - main toolbar is always visible at top

    const handleContextMenu = (e: React.MouseEvent, pageNumber: number) => {
        e.preventDefault();
        const container = containerRefs.current[pageNumber];
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        setContextMenu({ x, y, pageNumber, clientX: e.clientX, clientY: e.clientY });
    };

    const handleCreatePolygonAtPosition = (x: number, y: number, pageNumber: number) => {
        onCreatePolygon?.({
            pageNumber,
            x: Math.max(0, Math.min(1, x - 0.084)),
            y: Math.max(0, Math.min(1, y - 0.0355)),
        });
        setContextMenu(null);
    };

    return (
        <div className="w-full h-full flex flex-col min-h-0 overflow-hidden">
            {/* Barra paginazione fissa */}
            <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <button
                        className="px-2 py-1 rounded bg-primary ring-1 ring-secondary text-secondary disabled:opacity-50"
                        onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
                        disabled={safePage <= 1}
                    >
                        {t('pdfViewer.prev')}
                    </button>
                    <span className="text-sm text-tertiary">{t('pdfViewer.page')} {safePage}/{numPages || "-"}</span>
                    <button
                        className="px-2 py-1 rounded bg-primary ring-1 ring-secondary text-secondary disabled:opacity-50"
                        onClick={() => setCurrentPage(Math.min(numPages || safePage, safePage + 1))}
                        disabled={!numPages || safePage >= numPages}
                    >
                        {t('pdfViewer.next')}
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    {/* Rotation controls */}
                    <div className="flex items-center gap-1 mr-2">
                        <button
                            className="px-2 py-1 rounded bg-primary ring-1 ring-secondary text-secondary text-sm"
                            onClick={handleRotateCounterClockwise}
                            title={t('pdfViewer.rotateLeft', { defaultValue: 'Rotate Left' })}
                        >
                            ↺
                        </button>
                        <button
                            className="px-2 py-1 rounded bg-primary ring-1 ring-secondary text-secondary text-sm"
                            onClick={handleRotateClockwise}
                            title={t('pdfViewer.rotateRight', { defaultValue: 'Rotate Right' })}
                        >
                            ↻
                        </button>
                    </div>
                    {/* Zoom controls */}
                    {showZoomControls && (
                        <div className="flex items-center gap-1 mr-2">
                            <button
                                className="px-2 py-1 rounded bg-primary ring-1 ring-secondary text-secondary disabled:opacity-50 text-sm"
                                onClick={handleZoomOut}
                                disabled={scale <= MIN_SCALE}
                                title={t('pdfViewer.zoomOut', { defaultValue: 'Zoom Out' })}
                            >
                                −
                            </button>
                            <button
                                className="px-2 py-1 rounded bg-primary ring-1 ring-secondary text-secondary text-sm min-w-[60px]"
                                onClick={handleZoomReset}
                                title={t('pdfViewer.zoomReset', { defaultValue: 'Reset Zoom' })}
                            >
                                {Math.round(scale * 100)}%
                            </button>
                            <button
                                className="px-2 py-1 rounded bg-primary ring-1 ring-secondary text-secondary disabled:opacity-50 text-sm"
                                onClick={handleZoomIn}
                                disabled={scale >= MAX_SCALE}
                                title={t('pdfViewer.zoomIn', { defaultValue: 'Zoom In' })}
                            >
                                +
                            </button>
                        </div>
                    )}
                    {!hideAddRectangle && (
                        <button
                            ref={addButtonRef}
                            className="px-3 py-1 rounded bg-brand-solid dark:text-white text-sm font-medium hover:bg-brand-solid_hover"
                            onClick={handleCreateClick}
                        >
                            + {t('pdfViewer.addRectangle')}
                        </button>
                    )}
                </div>
            </div>

            {/* Floating toolbar disabled - main toolbar is always visible at top */}

            {/* Area documento - mostra solo la pagina corrente */}
            <div ref={scrollContainerRef} className="flex-1 overflow-auto">
                <Document key={fileUrl} file={fileUrl} onLoadSuccess={handleDocumentLoad} loading={<div className="p-6">{t('pdfViewer.loadingPdf')}</div>}>
                    {numPages > 0 && safePage > 0 && safePage <= numPages && fileUrl && (
                        <div
                            className="relative flex justify-center min-w-fit"
                            onContextMenu={(e) => handleContextMenu(e, safePage)}
                        >
                            <div
                                ref={(el) => {
                                    containerRefs.current[safePage] = el;
                                    // Update dimensions when ref is set and container is available
                                    if (el) {
                                        // Use requestAnimationFrame to ensure DOM is fully updated
                                        requestAnimationFrame(() => {
                                            const width = el.clientWidth;
                                            const height = el.clientHeight;
                                            if (width > 0 && height > 0) {
                                                setPageDimensions((prev) => ({
                                                    ...prev,
                                                    [safePage]: { width, height },
                                                }));
                                            }
                                        });
                                    }
                                }}
                                className="relative"
                                style={{ width: "fit-content" }}
                            >
                                <Page
                                    pageNumber={safePage}
                                    scale={scale}
                                    rotate={rotation}
                                    renderAnnotationLayer
                                    renderTextLayer
                                    onRenderSuccess={() => handlePageRenderSuccess(safePage)}
                                />
                                {/* Overlay - only render when renderOverlay is provided and dimensions are available */}
                                {renderOverlay && pageDimensions[safePage] && (
                                    <div className="absolute inset-0 pointer-events-auto z-10">
                                        {renderOverlay({
                                            pageNumber: safePage,
                                            pageWidth: pageDimensions[safePage].width,
                                            pageHeight: pageDimensions[safePage].height,
                                            containerRef: { current: containerRefs.current[safePage] } as React.RefObject<HTMLDivElement>,
                                        })}
                                    </div>
                                )}
                                {/* Polygons sono gestiti dall'overlay (es. Fabric.js) */}
                            </div>
                        </div>
                    )}
                </Document>
            </div>

            {/* Context Menu */}
            {contextMenu && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setContextMenu(null)}
                    />
                    <div
                        className="fixed z-50 bg-primary border border-secondary rounded-lg shadow-lg p-1"
                        style={{
                            left: `${contextMenu.clientX}px`,
                            top: `${contextMenu.clientY}px`,
                        }}
                    >
                        <button
                            className="px-3 py-2 text-sm text-primary hover:bg-secondary rounded w-full text-left"
                            onClick={() => {
                                handleCreatePolygonAtPosition(contextMenu.x, contextMenu.y, contextMenu.pageNumber);
                            }}
                        >
                            {t('pdfViewer.addRectangleHere')}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}


